/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect } from "react";
import styled from "styled-components";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import { ROUTES } from "../constants/constants";
import Text from "../components/ui/Text";
import { useParams } from "react-router-dom";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import { useGetOnlineMeeting } from "../lib/react-query/useGetOnlineMeeting";
import LoadingIndicator from "../components/LoadingIndicator";
import Icon from "../components/ui/Icon";
import Title from "../components/ui/Title";
import Button from "../components/ui/Button";
import { isSafeRedirectUrl } from "../utils/sanitize";

const StyledOnlineMeetingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
`;

const StyledOnlineMeetingHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const StyledOnlineMeetingDescription = styled((props) => <Text {...props} />)`
  max-width: 450px;
  margin: 0 auto;
  text-align: center;
`;

const POLLING_INTERVAL = 5000;

const OnlineMeeting = () => {
  const { onlineMeetingId, clearParams, hash } = useWidgetCallbackParams();
  const params = useParams();
  const id = onlineMeetingId || params?.id || null;

  const { isLoading, data: onlineMeeting, error } = useGetOnlineMeeting(id, hash, {
    refetchInterval: (query) => {
      if (query.state.status === "error") {
        return false;
      }

      const hasJoinUrl = !!query.state.data?.join_url;
      return hasJoinUrl ? false : POLLING_INTERVAL;
    },
  });

  const joinUrl = isSafeRedirectUrl(onlineMeeting?.join_url) ? onlineMeeting.join_url : null;
  const hasJoinUrl = !!joinUrl;

  useEffect(() => {
    if (!isLoading) {
      clearParams();
    }
  }, [clearParams, isLoading]);

  if (isLoading) {
    return (
      <Container>
        <NavigationHeader title="Online Meeting" back={ROUTES.HOME} />
        <LoadingIndicator page />
      </Container>
    );
  }

  if (error) {
    return <Container>
      <StyledOnlineMeetingContainer>
        <StyledOnlineMeetingHeader>
          <Icon.BlockIcon size={108} color="secondary" />
          <Title size="h3">Unable to Join</Title>
        </StyledOnlineMeetingHeader>

        <StyledOnlineMeetingDescription color="dark">
          We couldn't grant you access to this meeting. This may be due to pending payment or booking restrictions. Please check your email for more details.
        </StyledOnlineMeetingDescription>
      </StyledOnlineMeetingContainer>
    </Container>
  }


  return (
    <Container>
      <NavigationHeader
        title="Online Meeting"
        back={ROUTES.HOME}
      />
      <StyledOnlineMeetingContainer>
        <StyledOnlineMeetingHeader>
          <Icon.OnlineMeetingIcon size={108} color="primary" />
          <Title size="h3">Welcome to Our Online Meeting</Title>
        </StyledOnlineMeetingHeader>
        {!hasJoinUrl && (
          <StyledOnlineMeetingDescription color="dark">
            The meeting has not started yet. Please give us a minute to join if the time and date is correct.
          </StyledOnlineMeetingDescription>
        )}
        <Button
          variant="primary"
          fullWidth
          disabled={!hasJoinUrl}
          href={joinUrl}
          target="_blank"
          title="Join the meeting"
        />
      </StyledOnlineMeetingContainer>
    </Container>
  );
};

export default OnlineMeeting;